import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError, DoorDashboard, GuestList, PerformanceAdmissions } from '../api';
import { admissionEndpoints, eventEndpoints } from '../api';
import { NotPermitted } from '../App';
import { usePermissions } from '../session';
import { Badge, Button, EmptyRow, ErrorNote, Panel, Table, formatWhen } from '../ui';
import { INPUT, inZone } from './event/shared';
import { dateWords } from '../terms';

/**
 * The door (docs/scanner.md §6, D-59): a performance's live admission
 * counts by gate and ticket type, arrivals by quarter hour, the rejection
 * log, the offline bundles out on devices (with revoke), and — for the
 * performance — which zones each gate admits and which ticket types belong
 * to which zone.
 */
export default function DoorPage() {
  const can = usePermissions();
  const [eventId, setEventId] = useState('');
  const [performanceId, setPerformanceId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const events = useQuery({ queryKey: ['events-list', 'PUBLISHED'], queryFn: () => eventEndpoints.list('PUBLISHED') });
  const performances = useQuery({ queryKey: ['event', eventId, 'performances'], queryFn: () => eventEndpoints.performances(eventId), enabled: Boolean(eventId) });
  const dashboard = useQuery<{ dashboard: DoorDashboard }, ApiError>({ queryKey: ['door', performanceId], queryFn: () => admissionEndpoints.dashboard(performanceId), enabled: Boolean(performanceId), refetchInterval: 5000 });
  // The list of who should be let in, beside the telemetry about who has been (D-199).
  const guests = useQuery<{ guest_list: GuestList }, ApiError>({ queryKey: ['guest-list', performanceId], queryFn: () => admissionEndpoints.guestList(performanceId), enabled: Boolean(performanceId), refetchInterval: 30_000 });
  if (!can('reports.view_attendance')) return <NotPermitted permission="reports.view_attendance" />;
  const d = dashboard.data?.dashboard;

  return (
    <div className="space-y-4">
      {error ? <ErrorNote>{error}</ErrorNote> : null}
      <Panel title="Door" description={`Live admissions for ${dateWords().a}, refreshed every five seconds while it is open.`}>
        <div className="flex flex-wrap items-end gap-3 px-5 py-4">
          <label className="text-sm">
            <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">Event</span>
            <select className={`mt-1 ${INPUT}`} value={eventId} onChange={(e) => { setEventId(e.target.value); setPerformanceId(''); }}>
              <option value="">Choose an event</option>
              {(events.data?.events ?? []).map((e) => (
                <option key={e.id} value={e.id}>
                  {e.title}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">{dateWords().One}</span>
            <select className={`mt-1 ${INPUT}`} value={performanceId} disabled={!eventId} onChange={(e) => setPerformanceId(e.target.value)}>
              <option value="">Choose {dateWords().a}</option>
              {(performances.data?.performances ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {inZone(p.starts_at, p.timezone)} {p.name_override ? `· ${p.name_override}` : ''}
                </option>
              ))}
            </select>
          </label>
          {d ? <span className="ml-auto text-xs text-ink-400">as of {formatWhen(d.computed_at)}</span> : null}
        </div>
        {dashboard.error ? <ErrorNote>{dashboard.error.message}</ErrorNote> : null}
        {d ? (
          <div className="grid gap-4 border-t border-line px-5 py-4 md:grid-cols-4">
            <Stat label="Admitted" value={`${d.totals.admitted}`} detail={`of ${d.totals.live_tickets} live tickets`} />
            <Stat label="Inside now" value={`${d.totals.inside}`} detail="re-entry venues count exits" />
            <Stat label="Scans" value={`${d.totals.scans}`} detail={d.by_result.map((r) => `${r.count} ${r.result.toLowerCase().replaceAll('_', ' ')}`).join(' · ') || 'none yet'} />
            <Stat label="No-shows so far" value={`${Math.max(0, d.totals.live_tickets - d.totals.admitted)}`} detail="live tickets not yet admitted" />
          </div>
        ) : null}
        {/*
          The only question anybody asks at ten past seven (item 72, D-309).
          Everything above is a count, and a count is about the past. When there
          is not enough to work a pace from, the reason is printed rather than a
          blank: an empty space where a number should be is read as a fault.
        */}
        {d ? (
          <div className="border-t border-line px-5 py-4">
            <div className="flex flex-wrap items-baseline gap-x-8 gap-y-2">
              <div>
                <span className="text-xs font-semibold tracking-wide text-ink-400 uppercase">Going through</span>
                <p className="tabular text-2xl font-semibold text-ink-900">
                  {d.pace.per_minute === null ? '—' : `${d.pace.per_minute}/min`}
                </p>
                {d.pace.per_minute_overall !== null && d.pace.per_minute !== null ? (
                  <p className="text-xs text-ink-400">{d.pace.per_minute_overall}/min across the whole door</p>
                ) : null}
              </div>
              <div>
                <span className="text-xs font-semibold tracking-wide text-ink-400 uppercase">Still to come</span>
                <p className="tabular text-2xl font-semibold text-ink-900">{d.pace.remaining}</p>
              </div>
              <div className="min-w-64">
                <span className="text-xs font-semibold tracking-wide text-ink-400 uppercase">Clear in</span>
                {d.pace.minutes_to_clear === null ? (
                  <p className="mt-1 max-w-sm text-sm text-ink-500">{d.pace.projection_withheld}</p>
                ) : (
                  <p className="tabular text-2xl font-semibold text-ink-900">
                    {d.pace.minutes_to_clear} min
                    <span className="ml-2 text-xs font-normal text-ink-400">at the current pace</span>
                  </p>
                )}
              </div>
            </div>
            {d.pace.quiet_gates.length > 0 ? (
              <div className="mt-3 rounded border border-gold/40 bg-warn-soft px-4 py-2 text-sm text-ink-900">
                {d.pace.quiet_gates.map((gate) => (
                  <p key={gate.gate}>
                    <strong>{gate.gate}</strong> let {gate.admitted} in and has admitted nobody for {gate.silent_minutes} minutes, while the others are still working.
                  </p>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
      </Panel>

      {d && d.slots.length > 0 ? (
        <Panel
          title="Entry times"
          description="Who is due when, and who has arrived. The question at the entrance is not how many were sold — it is whether the next twenty minutes' worth have started turning up."
        >
          <Table headers={['Slot', 'Sold', 'Arrived', 'Capacity', '']}>
            {d.slots.map((slot) => (
              <tr key={slot.slot_id} className={`border-b border-line last:border-0 ${slot.current ? 'bg-accent-soft' : ''}`}>
                <td className="tabular px-5 py-3 text-sm text-ink-900">
                  {formatWhen(slot.starts_at)}
                  {slot.current ? <Badge tone="accent">now</Badge> : null}
                </td>
                <td className="tabular px-5 py-3 text-sm text-ink-600">{slot.expected}</td>
                <td className="tabular px-5 py-3 text-sm text-ink-600">{slot.arrived}</td>
                <td className="tabular px-5 py-3 text-xs text-ink-400">{slot.capacity}</td>
                <td className="px-5 py-3">
                  {slot.expected === 0 ? (
                    <span className="text-xs text-ink-400">nobody booked</span>
                  ) : slot.arrived >= slot.expected ? (
                    <Badge tone="ok">all in</Badge>
                  ) : (
                    <span className="text-xs text-ink-500">{slot.expected - slot.arrived} to come</span>
                  )}
                </td>
              </tr>
            ))}
          </Table>
        </Panel>
      ) : null}

      {performanceId && (guests.data?.guest_list.parties.length ?? 0) > 0 ? (
        <Panel
          title="Guest list"
          description={`Everyone comped onto this ${dateWords().one}, by surname. Each one holds a real ticket, so the scanner admits them like anybody else — this is the paper fallback, not a second way in.`}
          actions={
            <Button onClick={() => window.print()}>Print</Button>
          }
        >
          <div className="flex flex-wrap gap-8 px-5 py-3 text-sm">
            <span className="text-ink-600">
              <strong className="tabular">{guests.data!.guest_list.totals.parties}</strong> parties
            </span>
            <span className="text-ink-600">
              <strong className="tabular">{guests.data!.guest_list.totals.tickets}</strong> tickets
            </span>
            <span className="text-ink-600">
              <strong className="tabular">{guests.data!.guest_list.totals.arrived}</strong> arrived
            </span>
          </div>
          <Table headers={['Name', 'List', 'Seats', 'Tickets', 'Arrived']}>
            {guests.data!.guest_list.parties.map((party) => (
              <tr key={party.order_id} className="border-b border-line last:border-0">
                <td className="px-5 py-3">
                  <div className="font-medium text-ink-900">{party.name ?? '—'}</div>
                  <div className="text-xs text-ink-400">{party.email ?? party.order_number}</div>
                </td>
                <td className="px-5 py-3 text-sm text-ink-600">{party.reason ?? '—'}</td>
                <td className="px-5 py-3 text-xs text-ink-600">{party.seats.join(', ') || '—'}</td>
                <td className="tabular px-5 py-3 text-sm text-ink-600">{party.tickets}</td>
                <td className="px-5 py-3">
                  {party.arrived === 0 ? (
                    <span className="text-xs text-ink-400">not yet</span>
                  ) : (
                    <Badge tone={party.arrived === party.tickets ? 'ok' : 'warn'}>
                      {party.arrived} of {party.tickets}
                    </Badge>
                  )}
                </td>
              </tr>
            ))}
          </Table>
        </Panel>
      ) : null}

      {d ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <Panel title="By gate">
            <Table headers={['Gate', 'Admitted', 'Re-scans', 'Rejected']}>
              {d.by_gate.length === 0 ? (
                <EmptyRow columns={4}>No scans yet.</EmptyRow>
              ) : (
                d.by_gate.map((g) => (
                  <tr key={g.gate_id ?? 'none'} className="border-b border-line last:border-0">
                    <td className="px-5 py-3 text-ink-900">{g.gate ?? 'Any gate'}</td>
                    <td className="tabular px-5 py-3 text-sm">{g.admitted}</td>
                    <td className="tabular px-5 py-3 text-sm">{g.re_scans}</td>
                    <td className="tabular px-5 py-3 text-sm">{g.rejected}</td>
                  </tr>
                ))
              )}
            </Table>
          </Panel>
          <Panel title="By ticket type">
            <Table headers={['Type', 'Live', 'Admitted', 'Arrivals']}>
              {d.by_ticket_type.map((t) => (
                <tr key={t.ticket_type ?? 'none'} className="border-b border-line last:border-0">
                  <td className="px-5 py-3 text-ink-900">{t.ticket_type ?? '—'}</td>
                  <td className="tabular px-5 py-3 text-sm">{t.live}</td>
                  <td className="tabular px-5 py-3 text-sm">{t.admitted}</td>
                  <td className="tabular px-5 py-3 text-sm text-ink-600">{t.live > 0 ? `${Math.round((t.admitted / t.live) * 100)}%` : '—'}</td>
                </tr>
              ))}
              {d.arrivals.length > 0 ? (
                <tr>
                  <td colSpan={4} className="px-5 py-3 text-xs text-ink-600">
                    Arrivals by quarter hour (UTC): {d.arrivals.map((a) => `${a.bucket.slice(11)} → ${a.admitted}`).join(' · ')}
                  </td>
                </tr>
              ) : null}
            </Table>
          </Panel>
          <Panel title="Accessibility" description="What today's ticket holders asked for, and the wheelchair spaces they booked — which is a request nobody has to type (D-297). Read it before entry opens, not when somebody is standing in front of you.">
            <Table headers={['Order', 'Guest', 'Seats', 'Needs']}>
              {(d.access_requests ?? []).length === 0 ? (
                <EmptyRow columns={4}>Nobody asked for anything, and no wheelchair spaces are booked.</EmptyRow>
              ) : (
                (d.access_requests ?? []).map((r) => (
                  <tr key={r.order_number} className="border-b border-line last:border-0 align-top">
                    <td className="px-5 py-3 font-mono text-xs whitespace-nowrap text-ink-600">{r.order_number}</td>
                    <td className="px-5 py-3 text-sm text-ink-900">
                      {r.customer ?? '—'}
                      <span className="block text-xs text-ink-400">
                        {r.tickets} ticket{r.tickets === 1 ? '' : 's'}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-sm text-ink-600">{r.seats ?? '—'}</td>
                    <td className="px-5 py-3 text-sm text-ink-900">
                      {/*
                        A booked wheelchair space is said first: it is the thing
                        with a physical consequence, because the chair has to go
                        somewhere, and it is the one nobody typed (D-297).
                      */}
                      {r.wheelchair_spaces > 0 ? (
                        <span className="block font-medium">
                          {r.wheelchair_spaces} wheelchair space{r.wheelchair_spaces === 1 ? '' : 's'} booked
                        </span>
                      ) : null}
                      {r.needs || (r.wheelchair_spaces > 0 ? '' : '—')}
                      {r.note ? <span className="mt-0.5 block text-xs text-ink-600 italic">{r.note}</span> : null}
                    </td>
                  </tr>
                ))
              )}
            </Table>
          </Panel>
          <Panel title="Recent rejections" description="The last fifty scans that did not admit, newest first.">
            <Table headers={['When', 'Result', 'Ticket', 'Gate', 'Reason']}>
              {d.recent_rejections.length === 0 ? (
                <EmptyRow columns={5}>None.</EmptyRow>
              ) : (
                d.recent_rejections.map((r) => (
                  <tr key={r.id} className="border-b border-line last:border-0">
                    <td className="px-5 py-3 text-xs whitespace-nowrap text-ink-600">{formatWhen(r.at)}</td>
                    <td className="px-5 py-3">
                      <Badge tone={r.result === 'RE_SCAN' || r.result === 'OFFLINE_CONFLICT' || r.result === 'NOT_YET_VALID' ? 'warn' : 'bad'}>{r.result}</Badge>
                      {r.offline ? <span className="ml-1 text-xs text-ink-400">offline</span> : null}
                    </td>
                    <td className="px-5 py-3 text-sm text-ink-900">
                      {r.seat_label ?? '—'} {r.short_code ? <span className="font-mono text-xs text-ink-400">{r.short_code}</span> : null}
                    </td>
                    <td className="px-5 py-3 text-sm text-ink-600">
                      {r.gate ?? 'any'} · {r.device}
                    </td>
                    <td className="px-5 py-3 text-xs text-ink-600">{r.reason_text ?? ''}</td>
                  </tr>
                ))
              )}
            </Table>
          </Panel>
          <Panel title="Offline bundles" description={`Devices holding this ${dateWords().one}'s tickets and verification key. Revoke one and the device wipes it at its next sync.`}>
            <Table headers={['Device', 'Version', 'Tickets', 'Issued', 'Expires', '']}>
              {d.bundles.length === 0 ? (
                <EmptyRow columns={6}>No bundles downloaded.</EmptyRow>
              ) : (
                d.bundles.map((b) => <BundleRow key={b.id} bundle={b} onError={setError} canRevoke={can('scan.offline_bundle')} />)
              )}
            </Table>
          </Panel>
        </div>
      ) : null}

      {performanceId ? <GateZonesPanel performanceId={performanceId} eventId={eventId} canConfigure={can('scan.configure')} onError={setError} /> : null}
    </div>
  );
}

function Stat({ label, value, detail }: { label: string; value: string; detail: string }) {
  return (
    <div className="rounded-lg border border-line bg-surface px-5 py-4">
      <p className="text-xs font-semibold tracking-wide text-ink-400 uppercase">{label}</p>
      <p className="tabular mt-1 text-3xl font-bold text-ink-900">{value}</p>
      <p className="mt-1 text-xs text-ink-600">{detail}</p>
    </div>
  );
}

function BundleRow({ bundle: b, onError, canRevoke }: { bundle: DoorDashboard['bundles'][number]; onError: (m: string) => void; canRevoke: boolean }) {
  const queryClient = useQueryClient();
  const revoke = useMutation({ mutationFn: () => admissionEndpoints.revokeBundle(b.id), onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['door'] }), onError: (c: ApiError) => onError(c.message) });
  return (
    <tr className="border-b border-line last:border-0">
      <td className="px-5 py-3 text-ink-900">{b.device_name}</td>
      <td className="tabular px-5 py-3 text-sm">v{b.bundle_version}</td>
      <td className="tabular px-5 py-3 text-sm">{b.ticket_count}</td>
      <td className="px-5 py-3 text-xs whitespace-nowrap text-ink-600">{formatWhen(b.generated_at)}</td>
      <td className="px-5 py-3 text-xs whitespace-nowrap text-ink-600">{b.revoked_at ? <Badge tone="bad">revoked</Badge> : formatWhen(b.expires_at)}</td>
      <td className="px-5 py-3 text-right">{canRevoke && !b.revoked_at ? <Button variant="danger" disabled={revoke.isPending} onClick={() => { if (window.confirm('Revoke this bundle? The device wipes its tickets and key at the next sync.')) revoke.mutate(); }}>Revoke</Button> : null}</td>
    </tr>
  );
}

function GateZonesPanel({ performanceId, eventId, canConfigure, onError }: { performanceId: string; eventId: string; canConfigure: boolean; onError: (m: string) => void }) {
  const queryClient = useQueryClient();
  const setup = useQuery<PerformanceAdmissions, ApiError>({ queryKey: ['admissions', 'performance', performanceId], queryFn: () => admissionEndpoints.performance(performanceId) });
  const ticketTypes = useQuery({ queryKey: ['event', eventId, 'ticket-types'], queryFn: () => eventEndpoints.ticketTypes(eventId), enabled: Boolean(eventId) });
  const [gateZones, setGateZones] = useState<Record<string, Set<string>>>({});
  const [rules, setRules] = useState<{ access_zone_id: string; ticket_type_id: string | null }[]>([]);
  useEffect(() => {
    if (!setup.data) return;
    const next: Record<string, Set<string>> = {};
    for (const row of setup.data.gate_zones) (next[row.gate_id] ??= new Set()).add(row.access_zone_id);
    setGateZones(next);
    setRules(setup.data.rules.map((r) => ({ access_zone_id: r.access_zone_id, ticket_type_id: r.ticket_type_id })));
  }, [setup.data]);
  const save = useMutation({
    mutationFn: async () => {
      await admissionEndpoints.setGateZones(performanceId, Object.entries(gateZones).map(([gate_id, zones]) => ({ gate_id, access_zone_ids: [...zones] })));
      await admissionEndpoints.setAccessRules(performanceId, rules);
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admissions', 'performance', performanceId] }),
    onError: (c: ApiError) => onError(c.message),
  });
  if (!setup.data) return null;
  const { gates, zones } = setup.data;
  const toggle = (gateId: string, zoneId: string) =>
    setGateZones((current) => {
      const next = { ...current, [gateId]: new Set(current[gateId] ?? []) };
      if (next[gateId]!.has(zoneId)) next[gateId]!.delete(zoneId);
      else next[gateId]!.add(zoneId);
      return next;
    });
  return (
    <Panel title={`Gates and zones for this ${dateWords().one}`} description={`Tick the zones each gate admits; add a rule per zone saying which ticket type belongs to it (a rule with no type means every ticket). A gate with no zones ticked, or ${dateWords().a} with no rules, admits everything. Gates and zones themselves are set on the venue page.`}>
      {zones.length === 0 || gates.length === 0 ? (
        <p className="px-5 py-4 text-sm text-ink-600">Add gates and zones on the venue page first; until then every gate admits every ticket.</p>
      ) : (
        <div className="space-y-4 px-5 py-4">
          <Table headers={['Gate', ...zones.map((z) => z.name)]}>
            {gates.map((g) => (
              <tr key={g.id} className="border-b border-line last:border-0">
                <td className="px-5 py-3 text-ink-900">{g.name}</td>
                {zones.map((z) => (
                  <td key={z.id} className="px-5 py-3">
                    <input type="checkbox" checked={gateZones[g.id]?.has(z.id) ?? false} disabled={!canConfigure} onChange={() => toggle(g.id, z.id)} aria-label={`${g.name} admits ${z.name}`} />
                  </td>
                ))}
              </tr>
            ))}
          </Table>
          <div>
            <p className="text-xs font-semibold tracking-wide text-ink-400 uppercase">Which tickets belong to which zone</p>
            <ul className="mt-2 space-y-2">
              {rules.map((r, i) => (
                <li key={i} className="flex flex-wrap items-center gap-2 text-sm">
                  <select className={INPUT} value={r.access_zone_id} disabled={!canConfigure} onChange={(e) => setRules((rs) => rs.map((x, j) => (j === i ? { ...x, access_zone_id: e.target.value } : x)))}>
                    {zones.map((z) => (
                      <option key={z.id} value={z.id}>
                        {z.name}
                      </option>
                    ))}
                  </select>
                  <span className="text-ink-600">admits</span>
                  <select className={INPUT} value={r.ticket_type_id ?? ''} disabled={!canConfigure} onChange={(e) => setRules((rs) => rs.map((x, j) => (j === i ? { ...x, ticket_type_id: e.target.value || null } : x)))}>
                    <option value="">every ticket</option>
                    {(ticketTypes.data?.ticket_types ?? []).map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                  {canConfigure ? (
                    <Button onClick={() => setRules((rs) => rs.filter((_, j) => j !== i))}>Remove</Button>
                  ) : null}
                </li>
              ))}
            </ul>
            {canConfigure ? (
              <div className="mt-3 flex gap-2">
                <Button onClick={() => setRules((rs) => [...rs, { access_zone_id: zones[0]!.id, ticket_type_id: null }])}>Add rule</Button>
                <Button variant="primary" disabled={save.isPending} onClick={() => save.mutate()}>
                  {save.isPending ? 'Saving…' : 'Save gates and zones'}
                </Button>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </Panel>
  );
}
